import React, { useEffect, useState } from "react";
import axios from "axios";
import { TrendingUp, Lock, Sparkles, Zap } from "lucide-react";

/**
 * Loyalty Tier Progress Bar — retention/upsell widget for the arbitrage dashboard.
 * Fetches user's tier progress and shows a beautiful progress bar with upgrade prompt.
 */
export default function LoyaltyTierProgress() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProgress = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;
        const { data } = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/arbitrage/tier-progress`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setData(data);
      } catch (_) {
        // fail silently — widget is optional
      } finally {
        setLoading(false);
      }
    };
    fetchProgress();
  }, []);

  if (loading || !data) return null;

  const cur = data.current_tier;
  const next = data.next_tier;

  return (
    <div
      data-testid="loyalty-tier-widget"
      className="rounded-2xl border border-purple-500/30 bg-gradient-to-br from-slate-900/80 via-purple-950/40 to-slate-900/80 backdrop-blur-sm p-5 mb-6 shadow-lg"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-purple-300" />
          <h3 className="text-sm font-semibold text-purple-200 tracking-wide uppercase">
            Loyalty Tier Progress
          </h3>
        </div>
        {data.has_invested && (
          <span
            data-testid="loyalty-current-tier"
            className="text-[11px] font-medium text-purple-100 px-2 py-0.5 rounded-full bg-purple-500/20 border border-purple-500/40"
          >
            {cur.name}
          </span>
        )}
      </div>

      {/* Progress bar */}
      <div className="relative h-2.5 rounded-full bg-slate-800/60 overflow-hidden mb-3">
        <div
          className="absolute top-0 left-0 h-full bg-gradient-to-r from-purple-500 via-pink-500 to-amber-400 transition-all duration-700 ease-out"
          style={{ width: `${data.progress_percent}%` }}
          data-testid="loyalty-progress-fill"
        />
      </div>

      {/* Tier chips */}
      <div className="flex justify-between mb-4">
        {data.all_tiers.map((tier) => (
          <div
            key={tier.id}
            className={`flex flex-col items-center gap-0.5 ${
              tier.unlocked ? "opacity-100" : "opacity-50"
            }`}
          >
            <div
              className={`w-6 h-6 rounded-full flex items-center justify-center border ${
                tier.current
                  ? "bg-purple-500 border-purple-300 text-white ring-2 ring-purple-400/60"
                  : tier.unlocked
                  ? "bg-purple-500/40 border-purple-400/50 text-purple-100"
                  : "bg-slate-800 border-slate-700 text-slate-500"
              }`}
            >
              {tier.unlocked ? (
                <TrendingUp className="w-3 h-3" />
              ) : (
                <Lock className="w-3 h-3" />
              )}
            </div>
            <span
              className={`text-[10px] font-medium ${
                tier.current
                  ? "text-purple-200"
                  : tier.unlocked
                  ? "text-purple-300/80"
                  : "text-slate-500"
              }`}
            >
              {tier.name.split(" ")[0]}
            </span>
          </div>
        ))}
      </div>

      {/* Upgrade prompt */}
      {data.is_maxed ? (
        <div className="flex items-center gap-2 text-sm text-amber-300 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/30">
          <Zap className="w-4 h-4" />
          <span>
            <b>Max tier unlocked!</b> You&rsquo;re earning up to {cur.roi_max_pct}% ROI on VIP investments.
          </span>
        </div>
      ) : !data.has_invested ? (
        <p
          data-testid="loyalty-prompt-new"
          className="text-sm text-slate-300 leading-relaxed"
        >
          Start with any package to unlock your first tier. Higher tiers automatically boost your
          ROI toward the max — from <b className="text-purple-300">Starter (3%)</b> all the way to{" "}
          <b className="text-amber-300">VIP Elite (14%)</b>.
        </p>
      ) : (
        <p
          data-testid="loyalty-prompt-upgrade"
          className="text-sm text-slate-300 leading-relaxed"
        >
          You&rsquo;re on <b className="text-purple-300">{cur.name}</b> tier. Upgrade to{" "}
          <b className="text-amber-300">{next.name}</b> to unlock{" "}
          <b>{next.tier_bonus_pct}% tier bonus</b> and earn up to{" "}
          <b className="text-emerald-300">{next.roi_max_pct}% ROI</b>.
        </p>
      )}

      {data.activity.completed_investments > 0 && (
        <p className="text-[11px] text-slate-500 mt-2">
          Loyalty boost: {data.activity.activity_factor_pct}% (from{" "}
          {data.activity.completed_investments} completed investment
          {data.activity.completed_investments === 1 ? "" : "s"})
        </p>
      )}
    </div>
  );
}
